/* These are turned into rocketship-validation objects.
 * Information on that is located in the readme for that.
 * https://bitbucket.helloworld.com/projects/ROC/repos/validator/browse
 */
const
    range = require('lodash/range'),
    isEmail = require('validator/lib/isEmail'),
    config = require('rocketship-config'),
    diffInYears = require('date-fns/differenceInYears/index'),
    { maxFields, displayCAConfirm } = {"_public":true,"maxFields":3,"displayCAConfirm":true},
    // This is used to protect downstream systems with databases that can't handle
    // characters wider than three bytes, like FE2 and HW Analytics.
    // https://jiradc.helloworld.com/browse/SCU-144
    /* eslint-disable-next-line no-control-regex -- We match every character
        less than 4 bytes wide here, which does intentionally include control
        characters.
    */
    no4ByteChars = /^[\u{000000}-\u{00FFFF}]*$/u,
    noEmail = /^((?!@).)*$/;

module.exports = {
    login: {
        email: {
            required: true,
            external: [isEmail],
            no4ByteChars,
        },
    },
    register: {
        first_name: {
            required: true,
            no4ByteChars,
            noEmail,
        },
        last_name: {
            required: true,
            no4ByteChars,
            noEmail,
        },
        email: {
            required: true,
            external: [isEmail],
            no4ByteChars,
        },
        'age.birth_day': { required: true },
        'age.birth_month': { required: true },
        'age.birth_year': { required: true },
        'guardian.first_name': {
            required: false,
            validateGuardian,
            no4ByteChars,
            noEmail,
        },
        'guardian.last_name': {
            required: false,
            validateGuardian,
            no4ByteChars,
            noEmail,
        },
        'guardian.email': {
            required: false,
            validateGuardianEmail,
            external: [isEmail],
            no4ByteChars,
        },
        rules: {
            // `isChecked` on its own, like other guards, is implicitly optional, i.e.
            // it's as if we are saying "if we get a value, it must look like a checked
            // checkbox", and `required` means "we must get a value" (and `false` is a
            // value), so we use both `required` and `isChecked` to fully require this
            // field to be checked.
            required: true,
            isChecked: true,
        },
    },
    viral: {
        to_email1: {
            required: true,
            external: [isEmail],
            no4ByteChars,
            // These validators run on the first field, but are smart enough to check
            // for errors in all TO email addresses.
            // Note: `referringSelf` may only be validated client-side if `email` is a
            // public field in the profile config, and it's not by default.
            referringSelf (value, key, obj) {
                const
                    validation = this,
                    toEmailFields = getFilledToEmailFields(obj);

                const referredSelf = toEmailFields.find(([key, email]) => email === obj.selfEmail);

                if (referredSelf) {
                    const [selfKey] = referredSelf;
                    validation.addError(selfKey, 'REFERRED_SELF');
                    return false;
                }

                return true;
            },
            duplicateReferral (value, key, obj) {
                const
                    validation = this,
                    toEmailFields = getFilledToEmailFields(obj),
                    seen = {};

                const duplicate = toEmailFields.find(([key, email]) => {
                    if (email in seen) return true;
                    seen[email] = true;
                    return false;
                });

                if (duplicate) {
                    const [duplicateKey] = duplicate;
                    validation.addError(duplicateKey, 'DUPLICATE');
                    return false;
                }

                return true;
            },
        },
        to_name1: {
            required: true,
            no4ByteChars,
        },
    },
    faqContact: {
        first_name: {
            required: true,
        },
        email: {
            required: true,
            external: [isEmail],
        },
        question: {
            required: true,
        },
        issue_type: {
            required: true,
        },
    },
    prelaunchEmail: {
        email: {
            required: true,
            external: [isEmail],
        },
        primaryOptIn: {
            required: true,
            isChecked: true,
        },
    },
    optOut: {
        email: {
            required: true,
            external: [isEmail],
        },
    },
    experiencePoll: {
        rating: { required: true, validateExperiencePoll },
    },
    gameScore: {
        score: {
            required: true,
            validateGameScore,
        },
    },
};

const viralGuards = module.exports.viral;

if (displayCAConfirm) {
    viralGuards.taf_confirm = { required: true, isChecked: true };
}

range(1, maxFields + 1).forEach((fieldNum) => {
    viralGuards['to_name' + fieldNum] = {
        ...viralGuards['to_name' + fieldNum],

        requiresField: 'to_email' + fieldNum,
    };
    viralGuards['to_email' + fieldNum] = {
        ...viralGuards['to_email' + fieldNum],

        requiresField: 'to_name' + fieldNum,
        external: [isEmail],
    };
});

function getFilledToEmailFields (obj) {
    return  Object.entries(obj)
    .filter(([key]) => key.startsWith('to_email'))
    // Filter out blanks.
    .filter(([key, email]) => !!email);
}

function validateGuardian (value, key, obj, opts) {
    const required = ['age.birth_year', 'age.birth_month', 'age.birth_day']
    .filter((e) => e in obj);

    if (required.length < 3) return true;

    const
        now = opts[0] || new Date(),
        birthDate = new Date(
            obj['age.birth_year'],
            obj['age.birth_month'] - 1,
            obj['age.birth_day'],
        );

    const age = diffInYears(now, birthDate);

    return age >= 18 || age < 13 || (age <= 18 && value);
}

function validateGuardianEmail (value, key, obj, opts) {
    const validation = this;

    const valid = validateGuardian(value, key, obj, opts);

    if (!valid) return valid;

    if (obj.email === obj['guardian.email']) {
        validation.addError(key, 'MATCHING_GUARDIAN_EMAIL');
        return false;
    }

    return true;
}

function validateExperiencePoll (value, key, obj, opt) {
    const validation = this;

    if (!(/^[1-5]$/).test(obj.rating)) {
        validation.addError(key, 'RATING IS NOT A VALID NUMBER');
        return false;
    }

    return true;
}

function validateGameScore (value, key, obj, opt) {
    const validation = this;

    if (!(/^[0-9]+$/).test(obj.score)) {
        validation.addError(key, 'SCORE IS NOT A VALID NUMBER');
        return false;
    }

    return true;
}
